import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

// Teal theme – refined for Company auth
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#f8fafc',
    fontSize: '0.9375rem',
    '&:hover': { bgcolor: '#f1f5f9' },
    '&.Mui-focused': { bgcolor: '#fff', boxShadow: '0 0 0 3px rgba(13, 148, 136, 0.12)' },
    '&.Mui-focused fieldset': { borderColor: '#0d9488', borderWidth: 2 },
    '&:hover fieldset': { borderColor: 'rgba(13, 148, 136, 0.5)' },
    transition: 'background-color 0.2s, box-shadow 0.2s',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0d9488' },
};

const selectSx = {
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0d9488' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(13, 148, 136, 0.5)' },
};

const primaryBtnSx = {
  background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #14b8a6 100%)',
  color: '#fff',
  borderRadius: 2.5,
  py: 1.4,
  fontWeight: 700,
  fontSize: '1rem',
  letterSpacing: '0.02em',
  boxShadow: '0 4px 16px rgba(13, 148, 136, 0.4)',
  transition: 'box-shadow 0.2s, transform 0.2s',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(13, 148, 136, 0.5)',
    transform: 'translateY(-1px)',
  },
  '&:active': { transform: 'translateY(0)' },
};

function CompanyAuth({ onClose, onAuthSuccess, defaultStep = 'login' }) {
  const [step, setStep] = useState(defaultStep);
  const [mobile, setMobile] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [resendCount, setResendCount] = useState(0);
  const [resendTimer, setResendTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [establishmentCode, setEstablishmentCode] = useState('');
  const [companyType, setCompanyType] = useState('');
  const [isBVOCCompany, setIsBVOCCompany] = useState(false);
  const [login, setLogin] = useState(''); // For login form - can be email or mobile
  const navigate = useNavigate();

  // Function to detect BVOC companies based on dropdown selection
  const detectBVOCCompany = (selectedType) => {
    const isBVOC = selectedType === 'bvoc';
    setIsBVOCCompany(isBVOC);
    return isBVOC;
  };

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  // Detect BVOC company when company type changes
  useEffect(() => {
    if (companyType) {
      detectBVOCCompany(companyType);
    }
  }, [companyType]);

  const sendOtp = async (forReset = false) => {
    setLoading(true);
    setError('');
    try {
      const endpoint = forReset ? '/api/company/forgot-password' : '/api/company/resend-otp';
      const res = await fetch(API_URL + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');
      setStep(forReset ? 'forgot-otp' : 'otp');
      setResendTimer(60);
      setResendCount(resendCount + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(mobile)) return setError('Enter a valid 10-digit mobile number');
    if (!companyName) return setError('Enter your company name');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError('Enter a valid email');
    if (!companyType) return setError('Please select company type');
    
    // Check if it's a BVOC company
    const isBVOC = detectBVOCCompany(companyType);
    
    // For NAPS/NATS companies, establishment code is required
    if ((companyType === 'naps' || companyType === 'nats') && !establishmentCode.trim()) {
      return setError(`Establishment code is required for ${companyType.toUpperCase()} companies`);
    }
    
    if (!password || password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPwd) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/company/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          mobile, 
          company_name: companyName, 
          password, 
          email, 
          establishment_code: establishmentCode.trim() || null,
          company_type: companyType
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Signup failed');
      
      // Company is now registered and verified automatically
      if (!data.token) {
        throw new Error('Login token not received from server');
      }
      let token = data.token;
      let user = data.user || { ...data, type: 'company' };
      localStorage.setItem('gogig_token', token);
      localStorage.setItem('gogig_user', JSON.stringify(user));
      if (onAuthSuccess) onAuthSuccess(token, user);
      navigate('/company/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate login input (email or mobile)
    if (!login) return setError('Enter your email address or mobile number');
    if (!password) return setError('Enter your password');
    
    // Basic validation for email or mobile
    const isEmail = login.includes('@');
    const isMobile = /^[0-9]{10}$/.test(login);
    
    if (!isEmail && !isMobile) {
      return setError('Please enter a valid email address or 10-digit mobile number');
    }
    
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/company/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password }),
      });
      
      if (!res.ok) {
        let data;
        try {
          data = await res.json();
        } catch (e) {
          throw new Error(`Server error: ${res.status}`);
        }
        
        if (res.status === 400) {
          setError(data.error || 'Invalid credentials or password.');
        } else {
          setError(data.error || `Login failed: ${res.status}`);
        }
        return;
      }
      
      const data = await res.json();
      const token = data.token || data.access;
      if (!token) {
        throw new Error('Login token not received from server');
      }
      let user = { ...(data.user || data), type: 'company' };
      localStorage.setItem('gogig_token', token);
      localStorage.setItem('gogig_user', JSON.stringify(user));
      if (onAuthSuccess) onAuthSuccess(token, user);
      navigate('/company/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        setError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) return setError('Enter a valid 6-digit OTP');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/company/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');
      if (onAuthSuccess) onAuthSuccess(data.token, { mobile });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{10}$/.test(mobile)) return setError('Enter a valid 10-digit mobile number');
    await sendOtp(true);
  };

  const handleForgotOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otp)) return setError('Enter a valid 6-digit OTP');
    setStep('reset');
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 6) return setError('Password must be at least 6 characters');
    if (password !== confirmPwd) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/company/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Reset failed');
      if (onAuthSuccess) onAuthSuccess(data.token, { mobile });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formCardSx = {
    bgcolor: '#fff',
    p: 3,
    maxHeight: step === 'signup' ? '55vh' : 'none',
    overflowY: step === 'signup' ? 'auto' : 'visible',
  };

  return (
    <div
      className="auth-modal-wrap"
      style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
        width: '100%',
        maxWidth: 420,
        margin: '0 auto',
        overflow: 'hidden',
      }}
    >
      {/* Teal header bar – no blob, just a straight bar */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)',
          px: 3,
          pt: 2.5,
          pb: 2,
          position: 'relative',
        }}
      >
        {onClose && (
          <IconButton
            className="auth-modal-close"
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 12,
              right: 8,
              color: 'rgba(255,255,255,0.95)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' },
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>
            Company
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
            Sign in or register your company
          </Typography>
        </Box>
        <Tabs
          value={step === 'signup' ? 1 : 0}
          onChange={(e, v) => setStep(v === 1 ? 'signup' : 'login')}
          centered
          sx={{
            mt: 1.5,
            minHeight: 40,
            '& .MuiTabs-indicator': { background: '#fff', height: 2 },
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 14 },
            '& .Mui-selected': { color: '#fff !important' },
          }}
        >
          <Tab label="Login" />
          <Tab label="Sign Up" />
        </Tabs>
      </Box>

      {/* Form area – flat white, no round blob */}
      {step === 'signup' && (
        <form onSubmit={handleSignup}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField fullWidth label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} inputProps={{ maxLength: 10 }} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} sx={inputSx} className="auth-input" />
            <FormControl fullWidth>
              <InputLabel sx={{ '&.Mui-focused': { color: '#0d9488' } }}>Company Type</InputLabel>
              <Select value={companyType} onChange={(e) => setCompanyType(e.target.value)} label="Company Type" sx={{ ...selectSx, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <MenuItem value="bvoc">BVOC (Auto-generated Establishment ID)</MenuItem>
                <MenuItem value="naps">NAPS (Manual Establishment ID)</MenuItem>
                <MenuItem value="nats">NATS (Manual Establishment ID)</MenuItem>
                <MenuItem value="staffing">Staffing (HRMS Dashboard)</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField
              fullWidth
              label={isBVOCCompany ? "Establishment Code (Optional - Auto-generated for BVOC)" : "Establishment Code"}
              value={establishmentCode}
              onChange={(e) => setEstablishmentCode(e.target.value)}
              helperText={isBVOCCompany ? "Leave empty for auto-generation" : "Required for NAPS/NATS. Optional for Staffing."}
              placeholder={isBVOCCompany ? "Will be auto-generated if empty" : "Enter establishment code"}
              sx={inputSx}
              className="auth-input"
            />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Confirm Password" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
            </Button>
          </Stack>
          </Box>
        </form>
      )}

      {step === 'login' && (
        <form onSubmit={handleLogin}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Email Address or Mobile Number"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              placeholder="Enter your email or 10-digit mobile number"
              helperText="You can login with either your email address or mobile number"
              sx={inputSx}
              className="auth-input"
            />
            <TextField fullWidth label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
            </Button>
            <Link
              component="button"
              variant="body2"
              type="button"
              onClick={() => setStep('forgot')}
              sx={{
                color: '#0d9488',
                fontWeight: 600,
                textAlign: 'center',
                '&:hover': { color: '#0f766e', textDecoration: 'underline' },
              }}
              className="auth-link"
            >
              Forgot Password?
            </Link>
          </Stack>
          </Box>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={handleOtp}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputProps={{ maxLength: 6 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
            </Button>
            {resendCount < 3 ? (
              <Button variant="outlined" disabled={resendTimer > 0} onClick={() => sendOtp()} sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600 }}>
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </Button>
            ) : (
              <Typography color="text.secondary" align="center">Resend locked. Try after 15 min.</Typography>
            )}
          </Stack>
          </Box>
        </form>
      )}

      {step === 'forgot' && (
        <form onSubmit={handleForgot}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField fullWidth label="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} inputProps={{ maxLength: 10 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </Stack>
          </Box>
        </form>
      )}

      {step === 'forgot-otp' && (
        <form onSubmit={handleForgotOtp}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField fullWidth label="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} inputProps={{ maxLength: 6 }} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
            </Button>
          </Stack>
          </Box>
        </form>
      )}

      {step === 'reset' && (
        <form onSubmit={handleReset}>
          <Box sx={formCardSx}>
          <Stack spacing={2.5}>
            <TextField fullWidth label="New Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={inputSx} className="auth-input" />
            <TextField fullWidth label="Confirm Password" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} sx={inputSx} className="auth-input" />
            {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
            <Button fullWidth variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Stack>
          </Box>
        </form>
      )}
    </div>
  );
}

export default CompanyAuth;